import React from 'react'
import { Box, Link, styled } from '@mui/material'
import { useTranslation, Trans } from 'react-i18next'
import { NoSearchFound } from '../../rsmCoreComponents/components/NoSearchFound/NoSearchFound'

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'underline',
}))

/**
 * UserManagementNoRecords Component
 *
 * Displays the empty state UI when no users exist in the User Management system.
 * Shows "No records found" message with guidance text and an "Add new myRSM user"
 * button that remains visible and functional in all empty states.
 *
 * Handles three empty state scenarios:
 * - Initial System Setup: First-time system use with no users configured
 * - Post-Deletion State: After removing all existing users
 * - No Search Results: When active filters return no matches
 *
 * The "Add new myRSM user" call-to-action button remains consistently visible
 * and fully functional across all empty states, ensuring users can always
 * initiate user creation regardless of current data state.
 */
const UserManagementNoRecords: React.FC = () => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '25rem',
        padding: 3,
        textAlign: 'center',
      }}
      role="region"
      aria-label={t(
        'UserManagement.EmptyStateRegion',
        'User management empty state',
      )}>
      <NoSearchFound
        title={t('UserManagement.NoRecordsFound', 'No records found')}
        subTitle={
          <Trans
            i18nKey="UserManagement.AddUserPrompt"
            defaults={t('UserManagement.AddUserPrompt')}
            components={[
              <StyledLink
                href="#"
                data-testid="UserManager_AddNewMyRSMUser_Link"
              />,
            ]}
            values={{
              appManager: t('UserManagement.AddNewMyRSMUser'),
            }}
          />
        }
      />
    </Box>
  )
}

export default UserManagementNoRecords
